/*
 * Copyright (c) Logan Group Services - Gaston Longhitano <gaston.longhitano@logangroupservices.com>
 * All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react';
import './GameDetails.scss';

import {useRecoilValue, useSetRecoilState, useRecoilState} from 'recoil';
import {
  currentGameInfoQuery, currentUserInfoQuery,
  gameHighlightScenesQuery,
} from '../recoil/selectors';
import {
  currentGameIDState,
  currentSceneIDState,
  currentScreenState, viewMoreHighlightState,
} from '../recoil/atoms';
import { getImagePath } from '../Helper';
import {INTRO_SCREEN} from '../recoil/state';

import {
  CarouselProvider,
  Slider,
  Slide,
  ButtonBack,
  ButtonNext,
} from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronLeft, faChevronRight} from "@fortawesome/free-solid-svg-icons";
import {useMediaQuery} from "react-responsive";

function GameDetails() {
  const setCurrentSceneID = useSetRecoilState(currentSceneIDState);
  const highlightScenes = useRecoilValue(gameHighlightScenesQuery);
  const currentGame = useRecoilValue(currentGameInfoQuery);
  const setCurrentScreen = useSetRecoilState(currentScreenState);
  const [isViewMore, setIsViewMore] = useRecoilState(viewMoreHighlightState);

  const isMobile = useMediaQuery({ maxWidth: 575 });

  const isLandscape = useMediaQuery({ orientation: 'landscape', maxWidth: 850});
  const maxNumberScenes = 4;
  const handleViewMore = () => {
    setIsViewMore(!isViewMore);
  };

  const handleSceneSelection = (event, sceneID) => {
    console.log(`${sceneID} Selected`);
    event.preventDefault();
    setCurrentSceneID(sceneID);
  };

  return (
        <div className="interactive-details" style={{backgroundColor: 'rgba(0,0,0,0.9)'}}>
          <div className="video-container">
            <div className="interactive-details__header mb-2">
              <h3><span className="text-warning">{currentGame.title}</span></h3>
              <p>
                <span className="text-warning">Story: </span>
                {currentGame.description}
              </p>
            </div>
            <div className="interactive-details__body mb-4">
              <h4 className="interactive-details__body__title text-warning">STORY HIGHLIGHTS</h4>
              { /*isLandscape &&*/ <ChaptersCarousel {...{highlightScenes, isMobile, handleSceneSelection, currentGame}}/> }
              {/*{ !isLandscape && <Chapters {...{highlightScenes, maxNumberScenes, handleSceneSelection, currentGame, isViewMore, handleViewMore}}/> }*/}
            </div>
            <div className="interactive-details__footer d-flex justify-content-between">
              <div className="interactive-details__footer__left">
                <div className="button button-primary" onClick={() => setCurrentScreen(INTRO_SCREEN) }>GO BACK</div>
              </div>
              <div className="interactive-details__footer__right">
                <SavedGameButton/>
                <StartGameButton />
              </div>
            </div>
          </div>
        </div>
  );
}

function StartGameButton() {
  const currentGame = useRecoilValue(currentGameInfoQuery);
  const setCurrentSceneID = useSetRecoilState(currentSceneIDState);

  return (
      <div className="button button-warning" onClick={() => setCurrentSceneID(currentGame.tree.id) }>START GAME</div>
  )
}

function SavedGameButton() {
  const currentUser = useRecoilValue(currentUserInfoQuery);
  const setCurrentSceneID = useSetRecoilState(currentSceneIDState);
  const currentGameID = useRecoilValue(currentGameIDState);

  return (
      <div  className="button button-success mr-2" onClick={() => setCurrentSceneID(getLatestWatchedSceneID(currentGameID, currentUser.history)) }>CONTINUE GAME</div>
  )
}

function getLatestWatchedSceneID(gameID, history) {
  const index = history.findIndex(o => o.gameID === gameID);

  return  index >= 0 ? history[index]['latestWatchedSceneID'] : ''
}

function Chapters({highlightScenes, maxNumberScenes, handleSceneSelection, currentGame, isViewMore, handleViewMore}) {
  return (
      <>
      <div className="interactive-details__body__chapters">
        {highlightScenes.slice(0,maxNumberScenes).map((scene, index) => (
            <div  className={`clickable interactive-details__body__chapter mb-2 ${index > maxNumberScenes-1 ? 'view-more--show': ''}`} key={index}>
              <a onClick={(e) => handleSceneSelection(e, scene.id)} href="#" className="no-decor w-100">
                <img className="w-100" src={
                  getImagePath(
                      scene.fragments[0].thumbnail,
                      currentGame.site,
                      currentGame.shootId,
                      scene.fragments[0].fragmentId)
                } alt={scene.fragments[0].title}/>
                <div className="interactive-details__body__chapter__bottom">
                  <h4 className="interactive-details__body__chapter__title">{scene.fragments[0].title}</h4>
                  <p className="interactive-details__body__chapter__text">{scene.text}</p>
                </div>
              </a>
            </div>
        ))}
      </div>
      {highlightScenes.length > maxNumberScenes &&
      <>
        <div className={`interactive-details__body__chapters expandable ${isViewMore ? 'view-more-wrapper': ''}`}>
          {highlightScenes.slice(maxNumberScenes).map((scene, index) => (
              <div  className="clickable interactive-details__body__chapter mb-2 view-more--show" key={index}>
                <a onClick={(e) => handleSceneSelection(e, scene.id)} href="#" className="no-decor w-100">
                  <img className="w-100" src={
                    getImagePath(
                        scene.fragments[0].thumbnail,
                        currentGame.site,
                        currentGame.shootId,
                        scene.fragments[0].fragmentId)
                  } alt={scene.fragments[0].title}/>
                  <div className="interactive-details__body__chapter__bottom">
                    <h4 className="interactive-details__body__chapter__title">{scene.fragments[0].title}</h4>
                    <p className="interactive-details__body__chapter__text">{scene.text}</p>
                  </div>
                </a>
              </div>
          ))}
        </div>
        <div className="text-center">
          <a onClick={handleViewMore} className="text-warning">View {isViewMore ? 'Less': 'More'}</a>
        </div>
      </>
      }
      </>
  )
}

function ChaptersCarousel({highlightScenes, isMobile, handleSceneSelection, currentGame}) {
  return (
      <div className="interactive-details__body__chapters-slider">
        {
          <CarouselProvider
              naturalSlideWidth={100}
              naturalSlideHeight={100}
              isIntrinsicHeight={true}
              totalSlides={highlightScenes.length}
              visibleSlides={ isMobile ? 1 : 4 }
              orientation={'horizontal'}
          >
            <ButtonBack>
              <FontAwesomeIcon icon={faChevronLeft} />
            </ButtonBack>
            <Slider>
              { highlightScenes.map((scene, index) => {
                return (
                    <Slide className='scene-selector-item' key={index} index={index}>
                      <a onClick={(e) => handleSceneSelection(e, scene.id)} href="#" className="no-decor w-100">
                        <div className="img-wrapper">
                          <img className="w-100" src={
                            getImagePath(
                                scene.fragments[0].thumbnail,
                                currentGame.site,
                                currentGame.shootId,
                                scene.fragments[0].fragmentId)
                          } alt={scene.fragments[0].title}/>
                        </div>
                        <div className="interactive-details__body__chapter__bottom">
                          <h4 className="interactive-details__body__chapter__title">{scene.fragments[0].title}</h4>
                          <p className="interactive-details__body__chapter__text">{scene.text}</p>
                        </div>
                      </a>
                    </Slide>
                )
              })}
            </Slider>
            <ButtonNext>
              <FontAwesomeIcon icon={faChevronRight} />
            </ButtonNext>
          </CarouselProvider>
        }
      </div>
  )
}

export default GameDetails;
